/**
 * Publishing a saved competition from anywhere in its admin area.
 *
 * Tournaments carry `published` themselves; leagues and americanos publish
 * per cycle, so their "competition" state is the cycle in view or, on pages
 * without one, the latest cycle (`latestCycleId` / `latestCyclePublished` on
 * the league/americano response). Ladders and meets have no draft state.
 *
 * Each PUT sends only the fields that make the entity publishable: the
 * tournament's settings without its roster or draw (the API treats a `teams`
 * array as a roster save) and a cycle without its divisions (the API leaves
 * membership and matches untouched when Divisions is null).
 */
export function useCompetitionPublish() {
  const publishableKinds = ['tournament', 'league', 'americano'];

  const target = (kind, item, cycle) => {
    if (!item) return null;
    if (kind === 'tournament') {
      return {id: item.id, name: item.name, published: item.published === true};
    }
    if (kind === 'league' || kind === 'americano') {
      if (cycle?.id) return {id: cycle.id, name: cycle.name, published: cycle.published === true};
      if (item.latestCycleId) {
        return {id: item.latestCycleId, name: item.latestCycleName, published: item.latestCyclePublished === true};
      }
    }
    return null;
  };

  const publish = async (kind, item, cycle) => {
    const t = target(kind, item, cycle);
    if (!t) return {ok: false, errors: {global: ['Nothing to publish yet.']}};

    if (kind === 'tournament') {
      const {updateItem, hasFormErrors, formErrors} = useCrudApi('api/v2/tournaments');
      const {teams: _teams, stages: _stages, ...settingsOnly} = item;
      await updateItem(item.id, {
        ...settingsOnly,
        startDate: item.startDate || null,
        endDate: item.endDate || null,
        published: true,
      });
      return {ok: !hasFormErrors.value, errors: formErrors.value};
    }

    const path = kind === 'league' ? 'api/v2/leagueCycles' : 'api/v2/americanoCycles';
    const {item: fetched, getItem, updateItem, hasItemErrors, hasFormErrors, formErrors} = useCrudApi(path);
    await getItem(t.id);
    if (hasItemErrors.value || !fetched.value) {
      return {ok: false, errors: {global: ['The cycle could not be loaded.']}};
    }
    const c = fetched.value;
    const body = kind === 'league'
      ? {
        name: c.name, leagueId: c.leagueId, startDate: c.startDate, endDate: c.endDate,
        published: true, open: c.open || false, rollOverCycleId: null, rollOverChange: 0,
      }
      : {
        name: c.name, americanoId: c.americanoId, startDate: c.startDate, endDate: c.endDate,
        published: true, rollOverCycleId: null,
      };
    await updateItem(t.id, body);
    return {ok: !hasFormErrors.value, errors: formErrors.value};
  };

  // X182: the join reference types that carry a publish state (JOIN_REF: leagueCycle 2, tournament 4, americanoCycle 6)
  // and the kind each one publishes as. Ladders (1), meets (5) and the club (0) have none.
  const referenceKinds = {2: 'league', 4: 'tournament', 6: 'americano'};

  /**
   * X182: publish the thing an invite link is for, from its join reference (the Invite settings dialog). `loadTournament`
   * returns the tournament's detail (the PUT sends its settings); cycles need only their id.
   */
  const publishByReference = async (referenceType, referenceId, loadTournament) => {
    const kind = referenceKinds[referenceType];
    if (!kind || !referenceId) return {ok: false, errors: {global: ['Nothing to publish yet.']}};
    if (kind === 'tournament') {
      const item = await loadTournament?.(referenceId);
      if (!item) return {ok: false, errors: {global: ['The tournament could not be loaded.']}};
      return publish('tournament', item, null);
    }
    return publish(kind, {id: null}, {id: referenceId, name: '', published: false});
  };

  return {publishableKinds, target, publish, referenceKinds, publishByReference};
}
